import { trpc } from '@/utils/trpc';
import { TenantDTO, UpdateTenantDTO } from '../dtos';
import { TenantTableData } from '../types';
import { notificationService } from '@shared/components/notifications';

/**
 * Transform tRPC tenant response to TenantDTO format
 * Maps ElectroDB's tenantId to client's id field
 */
interface TrpcTenantResponse {
  tenantId: string;
  name: string;
  description?: string;
  code?: string;
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  version?: number;
}

/**
 * Helper function to check if error should be retried
 * Don't retry on 4xx client errors, but retry on server errors
 */
function shouldRetryError(failureCount: number, error: unknown): boolean {
  // Type-safe check for httpStatus property
  const httpStatus =
    error && typeof error === 'object' && 'data' in error
      ? (error.data as { httpStatus?: number } | null | undefined)?.httpStatus
      : undefined;

  // Don't retry on 4xx errors
  if (httpStatus && httpStatus >= 400 && httpStatus < 500) {
    return false;
  }

  // Retry up to 3 times for other errors
  return failureCount < 3;
}

function mapToTenantDTO(tenant: TrpcTenantResponse): TenantDTO {
  return {
    id: tenant.tenantId,
    name: tenant.name,
    description: tenant.description,
    code: tenant.code,
    createdAt: tenant.createdAt,
    updatedAt: tenant.updatedAt,
  };
}

// Query hook for fetching all tenants with cursor-based pagination
export const useTenants = (limit: number = 20) => {
  return trpc.tenant.list.useInfiniteQuery(
    { limit },
    {
      getNextPageParam: (lastPage) => lastPage.cursor,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: shouldRetryError,
    }
  );
};

// Legacy query hook for fetching all tenants (non-paginated)
// Kept for backward compatibility with existing components
export const useTenantsLegacy = () => {
  const query = trpc.tenant.list.useQuery(
    { limit: 100 }, // Fetch all for legacy compatibility
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: shouldRetryError,
    }
  );

  // Transform the response to match legacy format (array of TenantDTO)
  return {
    ...query,
    data: query.data?.items.map(mapToTenantDTO),
  };
};

// Query hook for fetching a single tenant
export const useTenant = (tenantId: string) => {
  const query = trpc.tenant.getById.useQuery(tenantId, {
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  // Transform the response to match TenantDTO format
  return {
    ...query,
    data: query.data ? mapToTenantDTO(query.data) : undefined,
  };
};

// Mutation hook for creating a tenant
export const useCreateTenant = () => {
  const utils = trpc.useUtils();

  return trpc.tenant.create.useMutation({
    onSuccess: (newTenant) => {
      // Invalidate all tenant queries to refetch with new data
      utils.tenant.list.invalidate();

      if (newTenant) {
        notificationService.success(`University "${newTenant.name}" created successfully`);
      }
    },
    onError: (error) => {
      const errorMessage = error?.message || 'Failed to create university';
      notificationService.error(errorMessage);
    },
  });
};

// Mutation hook for updating a tenant
export const useUpdateTenant = () => {
  const utils = trpc.useUtils();

  const mutation = trpc.tenant.update.useMutation({
    onSuccess: (updatedTenant) => {
      if (updatedTenant && updatedTenant.tenantId) {
        // Invalidate all tenant queries to refetch
        // We invalidate instead of setData because the update response is Partial
        utils.tenant.getById.invalidate(updatedTenant.tenantId);
        utils.tenant.list.invalidate();

        notificationService.success(`University "${updatedTenant.name}" updated successfully`);
      }
    },
    onError: (error) => {
      const errorMessage = error?.message || 'Failed to update university';
      notificationService.error(errorMessage);
    },
  });

  // Wrapper to maintain backward compatibility with existing component interface
  // Components call: mutate({ tenantId, data })
  // tRPC expects: mutate({ id, name?, description?, code? })
  return {
    ...mutation,
    mutate: (variables: { tenantId: string; data: UpdateTenantDTO }) => {
      mutation.mutate({
        id: variables.tenantId,
        ...variables.data,
      });
    },
    mutateAsync: async (variables: { tenantId: string; data: UpdateTenantDTO }) => {
      return mutation.mutateAsync({
        id: variables.tenantId,
        ...variables.data,
      });
    },
  };
};

// Mutation hook for deleting a tenant
export const useDeleteTenant = () => {
  const utils = trpc.useUtils();

  return trpc.tenant.delete.useMutation({
    onSuccess: (_result, variables) => {
      // Remove the tenant from cache
      // variables.id is the ID that was passed to mutate({ id })
      utils.tenant.getById.invalidate(variables.id);

      // Invalidate all tenant list queries to refetch
      utils.tenant.list.invalidate();

      notificationService.success('University deleted successfully');
    },
    onError: (error) => {
      const errorMessage = error?.message || 'Failed to delete university';
      notificationService.error(errorMessage);
    },
  });
};

// Computed hook for transforming tenant data for table display
export const useTenantsTableData = () => {
  const { data: tenants, ...queryResult } = useTenantsLegacy();

  const tableData: TenantTableData[] = (tenants || []).map((tenant) => ({
    id: tenant.id,
    name: tenant.name,
    description: tenant.description,
    code: tenant.code,
    createdAt: tenant.createdAt,
  }));

  return {
    data: tableData,
    ...queryResult,
  };
};
